import { SearchType } from './constants';
import type { SearchSuggestion, Suggestion, SuggestionItem } from './contracts';
import { SearchSuggestionWithType } from './contracts/SearchSuggestion';
import { highlightText } from './utils';

const savedSuggestionsKey = 'CVSavedSuggestions';

export function setSavedSuggestions(recentSearches: SearchSuggestion[]) {
  try {
    localStorage.setItem(savedSuggestionsKey, JSON.stringify(recentSearches));
  } catch (e) {
    console.error('Failed to save suggestion to local storage', e);
  }
}

function getSuggestionsFromLocalStorage(): SearchSuggestion[] {
  return JSON.parse(localStorage.getItem(savedSuggestionsKey) ?? '[]');
}

function getRecentSearches(): SearchSuggestionWithType[] {
  const savedSuggestions = getSuggestionsFromLocalStorage();

  return savedSuggestions.map(recentSearch => ({
    ...recentSearch,
    searchType: SearchType.RecentSearch
  }));
}

function removeLegacySearches(
  savedSearches: SearchSuggestion[]
): SearchSuggestion[] {
  const filteredSearches = savedSearches.filter(
    currentItem => !currentItem.hasOwnProperty('legacyFilters')
  );

  if (savedSearches.length !== filteredSearches.length) {
    console.error('Homepage legacyFilters removed'); // For visibility in datadog
  }

  setSavedSuggestions(filteredSearches);

  return filteredSearches;
}

export function getInitialRecentSearches(): SearchSuggestionWithType[] {
  const savedSuggestions = getSuggestionsFromLocalStorage();

  return removeLegacySearches(savedSuggestions).map(recentSearch => ({
    ...recentSearch,
    searchType: SearchType.RecentSearch
  }));
}

function isSearchSuggestion(
  v: SearchSuggestion | Suggestion
): v is SearchSuggestion {
  return (
    'buildKey' in v &&
    'filters' in v &&
    'searchType' in v &&
    v.searchType === SearchType.TextSearch
  );
}

export function saveSuggestionToLocalStorage(itemToSave: SuggestionItem) {
  const recentSearches = getRecentSearches();
  if (!isSearchSuggestion(itemToSave)) {
    return recentSearches;
  }

  const formattedItemToSave: SearchSuggestion = {
    buildKey: itemToSave.buildKey,
    filters: itemToSave.filters,
    highlightedText: itemToSave.highlightedText,
    isSavedSuggestion: true,
    text: itemToSave.text
  };

  const existsInLocalStorage = recentSearches.some(
    (suggestion: SearchSuggestion) =>
      suggestion.text === formattedItemToSave.text
  );

  if (!existsInLocalStorage) {
    recentSearches.unshift({
      ...formattedItemToSave,
      searchType: SearchType.RecentSearch
    });
    setSavedSuggestions(recentSearches);
  }

  return getRecentSearches();
}

export function removeSuggestionFromLocalStorage(
  removedSuggestionText: string,
  inputValue: string = ''
): SearchSuggestionWithType[] {
  const updatedRecentSearches = getRecentSearches().filter(
    (suggestion: SearchSuggestion) => {
      return (
        suggestion.text.toLowerCase() !== removedSuggestionText.toLowerCase()
      );
    }
  );
  setSavedSuggestions(updatedRecentSearches);

  return updatedRecentSearches.map(({ text, ...rest }) => ({
    ...rest,
    searchType: SearchType.RecentSearch,
    text,
    highlightedText: highlightText(text, inputValue)
  }));
}
